import { useEffect, useState, useMemo } from "react";
import { useMode } from "../context/ModeContext";

/**
 * PixelArtBox — a 12×12 grid of pixels that morphs through a rotating set
 * of recognizable shapes (heart, star, lightning, sparkle, smiley, brush)
 * with random twinkle frames in between. The pixel color is bound to the
 * active mode's accent so the box always feels native to the realm.
 *
 * No canvas — pure CSS grid of small divs animated via opacity transitions.
 * The pattern advances every ~2.5 s, picking a new shape and rotating
 * through twinkle frames in between for a "spark of creativity" feel.
 */
const GRID = 12;

/* 12×12 pixel-art patterns. 1 = lit, 0 = dim. */
const PATTERNS = {
  heart: [
    "000110011000",
    "001111111100",
    "011111111110",
    "011111111110",
    "011111111110",
    "001111111100",
    "000111111000",
    "000011110000",
    "000001100000",
    "000000000000",
    "000000000000",
    "000000000000",
  ],
  star: [
    "000000110000",
    "000001111000",
    "000011111100",
    "011111111111",
    "001111111110",
    "000111111100",
    "000111111100",
    "000111001100",
    "000110000110",
    "000110000110",
    "000110000110",
    "000000000000",
  ],
  lightning: [
    "000001111000",
    "000011110000",
    "000111100000",
    "001111000000",
    "011111111000",
    "000011110000",
    "000111100000",
    "001111000000",
    "011110000000",
    "011100000000",
    "000000000000",
    "000000000000",
  ],
  sparkle: [
    "000001100000",
    "000011110000",
    "000111111000",
    "011111111110",
    "001111111100",
    "000011110000",
    "000111111000",
    "001111111100",
    "011111111110",
    "000111111000",
    "000011110000",
    "000001100000",
  ],
  smiley: [
    "000011110000",
    "001111111100",
    "011111111110",
    "011001100110",
    "011001100110",
    "011111111110",
    "011111111110",
    "011100001110",
    "011110011110",
    "001111111100",
    "000111111000",
    "000011110000",
  ],
  brush: [
    "000000000110",
    "000000001110",
    "000000011110",
    "000000111100",
    "000001111000",
    "000011110000",
    "000111100000",
    "001111000000",
    "011110000000",
    "111100000000",
    "111000000000",
    "110000000000",
  ],
};

const PATTERN_KEYS = Object.keys(PATTERNS);
const _r = (min, max) => Math.random() * (max - min) + min;

function makeTwinkle() {
  // Random sparkle frame — ~30% of cells lit at random.
  return Array.from({ length: GRID }, () =>
    Array.from({ length: GRID }, () => (Math.random() < 0.3 ? "1" : "0")).join("")
  );
}

function parsePattern(rows) {
  // Returns flat array of "1"/"0" of length GRID*GRID.
  return rows.flatMap((row) => row.split(""));
}

export default function PixelArtBox({ size = 180 }) {
  let accent = "#FF4F27";
  try {
    const m = useMode();
    accent = m.config?.accent || accent;
  } catch (_) { /* outside provider */ }

  const [pattern, setPattern] = useState(() => parsePattern(PATTERNS[PATTERN_KEYS[0]]));

  // Rotate through patterns + twinkles every 2.5 s.
  useEffect(() => {
    let i = 0;
    let showTwinkle = false;
    const tick = () => {
      if (showTwinkle) {
        setPattern(parsePattern(makeTwinkle()));
      } else {
        // Pick a NEW pattern that isn't the last one for visible morph.
        const candidates = PATTERN_KEYS.filter((_, k) => k !== i);
        const pick = candidates[Math.floor(Math.random() * candidates.length)];
        i = PATTERN_KEYS.indexOf(pick);
        setPattern(parsePattern(PATTERNS[pick]));
      }
      showTwinkle = !showTwinkle;
    };
    const id = setInterval(tick, 1800);
    return () => clearInterval(id);
  }, []);

  // Per-cell jitter so the lit cells don't all light up perfectly in sync.
  const jitters = useMemo(
    () => Array.from({ length: GRID * GRID }, () => _r(0, 0.6).toFixed(2)),
    []
  );

  return (
    <div
      className="relative h-full p-5 rounded-3xl bg-white border border-ink-200/70 overflow-hidden flex flex-col"
      data-testid="pixel-art-box"
    >
      <div className="flex items-center justify-between mb-3">
        <p
          className="text-[10px] font-bold uppercase tracking-[0.22em]"
          style={{ color: accent, transition: "color 500ms ease-out" }}
        >
          Spark
        </p>
        <span className="text-[10px] text-ink-500">live pixels</span>
      </div>
      <div
        className="grid gap-[2px] mx-auto"
        style={{
          gridTemplateColumns: `repeat(${GRID}, 1fr)`,
          width:  `min(${size}px, 100%)`,
          aspectRatio: "1 / 1",
        }}
        aria-hidden="true"
      >
        {pattern.map((cell, idx) => (
          <div
            key={idx}
            className="rounded-[2px]"
            style={{
              background: cell === "1" ? accent : "#0a0a0a08",
              opacity:    cell === "1" ? 0.9 : 1,
              transition: `background 350ms ease-out ${jitters[idx]}s, opacity 350ms ease-out ${jitters[idx]}s`,
            }}
          />
        ))}
      </div>
      <p className="text-[10px] text-ink-500 mt-3 text-center leading-tight">
        XEA never stops imagining
      </p>
    </div>
  );
}
